import React, { useState } from "react";
import * as Dropdown from "@radix-ui/react-dropdown-menu";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../contexts/AuthContext";
import Button from "./ui/Button";
import Lottie from "lottie-react";
import loadingAnimation from "../assets/lottie/loading.json";
import {
  generateMessage,
  uploadImage,
  dalleGenerate,
  dalleVariation,
  getSubscriptionStatus,
  API_URL,
  dalleEdit,
} from "../utils/api";

// CSS for greeting card styling
const cardStyles = `
  .greeting-card-preview {
    width: 320px;
    height: 320px;
    border-radius: 12px;
    box-shadow: 
      0 8px 32px rgba(0, 0, 0, 0.12),
      0 2px 8px rgba(0, 0, 0, 0.08),
      inset 0 1px 0 rgba(255, 255, 255, 0.15);
    border: 1px solid rgba(255, 255, 255, 0.2);
    overflow: hidden;
    position: relative;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05));
    backdrop-filter: blur(10px);
    transition: all 0.3s ease;
  }
  
  .greeting-card-preview:hover {
    transform: translateY(-4px);
    box-shadow: 
      0 16px 48px rgba(0, 0, 0, 0.15),
      0 4px 12px rgba(0, 0, 0, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);
  }
  
  .greeting-card-preview::before {
    content: '';
    position: absolute;
    top: 8px;
    left: 8px;
    right: 8px;
    bottom: 8px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    pointer-events: none;
    z-index: 1;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.textContent = cardStyles;
  document.head.appendChild(styleSheet);
}

/* ===========================================================
   Constants
=========================================================== */

const DEFAULT_TONE = "Funny";
const DEFAULT_RECIPIENT = "Friend";

const TYPES = [
  "Graduation",
  "Birthday",
  "Anniversary",
  "Thank You",
  "Congrats",
  "Good Luck",
  "Leaving",
  "Christmas",
  "Valentine",
];

const TONES = ["Funny", "Romantic", "Casual", "Inspirational"];

const RECIPIENTS = [
  "Friend",
  "Family",
  "Coworker",
  "Parent",
  "Partner",
  "Sibling",
  "Child",
  "Teacher",
  "Boss",
];

// Card canvas is 600x400 (3:2 landscape), matches gpt-image-1's 1536x1024 ratio
// Falls back to DALL-E 3 (1792x1024) if gpt-image-1 unavailable
const SIZES = ["1536x1024", "1024x1024", "1792x1024"];

const STYLES = [
  "Modern",
  "Ghibli",
  "Children's Crayon",
  "Line Drawing",
  "Traditional",
  "Watercolor",
  "Minimalist",
  "Artistic",
  "Photographic",
  "Cartoon",
  "Abstract"
];

/**
 * Helper – generate images via DALL-E 3 (or edit if file provided)
 */
async function createImageJobs({ file, prompt, size }) {
  console.log("[createImageJobs] Starting with:", { hasFile: !!file, prompt: prompt?.substring(0, 50), size });
  
  if (file) {
    // When user uploads a photo, use DALL-E edit to transform it
    const fd = new FormData();
    fd.append("image", file);
    fd.append("prompt", prompt);
    fd.append("size", size);
    console.log("[createImageJobs] Using dalleEdit for uploaded image");
    // No mask provided - this will use DALL-E 3 for full image transformation
    const res = await dalleEdit(fd);
    console.log("[createImageJobs] dalleEdit response:", res.data);
    const resultUrls = res.data.public_url ? [res.data.public_url] : [];
    console.log("[createImageJobs] Returning URLs:", resultUrls);
    return resultUrls;
  } else {
    // When generating from text only, use DALL-E generate
    const fd = new FormData();
    fd.append("prompt", prompt);
    fd.append("size", size);
    console.log("[createImageJobs] Using dalleGenerate for new image");
    const res = await dalleGenerate(fd);
    console.log("[createImageJobs] dalleGenerate response:", res.data);
    const resultUrls = res.data.public_url ? [res.data.public_url] : [];
    console.log("[createImageJobs] Returning URLs:", resultUrls);
    return resultUrls;
  }
}

/**
 * Variation selector overlay - optimized for single AI-generated card
 */
function VariationSelector({ variations, loading, onSelect, onRegenerate }) {
  // Helper function to ensure full URL
  const getFullUrl = (url) => {
    if (!url) return '';
    // If it's already a full URL (from DALL-E), use it as is
    if (url.startsWith('http')) return url;
    // If it's a relative URL from our backend, prepend API_URL
    return url.startsWith('/') ? `${API_URL}${url}` : `${API_URL}/${url}`;
  };

  const hasVariations = variations && variations.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        className="mx-auto w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-6 shadow-2xl"
        style={{ maxHeight: "90vh" }}
      >
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">🎨 Your Cover is Ready!</h2>
          <p className="text-gray-600">Click the image to use it, or regenerate for a new design</p>
        </div>
        
        {loading ? (
          <div className="flex flex-col items-center justify-center py-8">
            <div className="w-32 h-32 mb-4">
              <Lottie 
                animationData={loadingAnimation} 
                loop={true}
                autoplay={true}
              />
            </div>
            <p className="text-lg text-purple-600 font-medium mb-1">Creating your personalized card...</p>
            <p className="text-gray-500 text-sm">This may take 1-2 minutes</p>
          </div>
        ) : hasVariations ? (
          <div className="flex justify-center mb-6">
            {variations.map((url, i) => (
              <div
                key={i}
                className="relative group cursor-pointer"
                onClick={() => {
                  console.log("[VariationSelector] Card clicked:", {
                    url,
                    fullUrl: getFullUrl(url),
                    onSelectType: typeof onSelect,
                  });
                  onSelect(url);
                }}
              >
                <div className="greeting-card-preview">
                  <img
                    src={getFullUrl(url)}
                    alt="Your AI-generated card cover"
                    className="w-full h-full object-contain transition-all duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg pointer-events-none"></div>
                  <div className="absolute bottom-2 left-2 right-2 text-center pointer-events-none">
                    <div className="bg-white/90 backdrop-blur-sm rounded-full px-3 py-1 text-sm font-medium text-gray-800 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      Click to use this design ✨
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500">No variations generated yet</p>
          </div>
        )}
        
        <div className="flex justify-center gap-4">
          <Button 
            onClick={onRegenerate} 
            disabled={loading}
            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-full hover:from-purple-700 hover:to-blue-700 transition-all shadow-lg"
          >
            🎲 {loading ? "Generating..." : "Generate New Design"}
          </Button>
          <Button
            variant="ghost"
            onClick={() => onSelect(null)}
            disabled={loading}
            className="px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-full hover:bg-gray-50 transition-all"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Main modal
 */
export default function CardBuilderModal({ onClose }) {
  const { token, user } = useAuthContext();
  const navigate = useNavigate();

  /* form state */
  const [type, setType] = useState("");
  const [tone, setTone] = useState(DEFAULT_TONE);
  const [recipient, setRec] = useState(DEFAULT_RECIPIENT);
  const [style, setStyle] = useState("Modern");
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [size, setSize] = useState("1536x1024"); // Landscape to match card canvas (600x400, 3:2 ratio)

  /* generation state */
  const [step, setStep] = useState("form");
  const [frontText, setFrontText] = useState("");
  const [insideText, setInsideText] = useState("");
  const [variations, setVariations] = useState([]);
  const [loading, setLoading] = useState(false);
  
  /* upgrade prompt state for Preview tier users */
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);

  /* -------------------------------------------------------
     Trigger generation
  ------------------------------------------------------- */
  const handleGenerate = async () => {
    // 1) Ensure user is signed in
    if (!token || !user) {
      onClose(); // Close the modal first
      navigate('/register?redirect=' + encodeURIComponent('/'), { 
        state: { fromAI: true, message: 'Create a free account to generate AI greeting cards' } 
      });
      return;
    }
    
    if (!type) {
      alert("Please select a card type.");
      return;
    }

    // 2) Check subscription status and AI photo limits
    try {
      const { data: statusData } = await getSubscriptionStatus();
      
      // Check if user has AI photo credits (Preview tier has 0)
      const aiPhotoLimit = statusData?.limits?.ai_photos_per_month || 0;
      const aiPhotoUsage = statusData?.usage?.ai_photos_per_month?.count || 0;
      
      if (aiPhotoLimit === 0 || aiPhotoUsage >= aiPhotoLimit) {
        // Show friendly upgrade prompt for Preview tier users
        setShowUpgradePrompt(true);
        return;
      }
    } catch (err) {
      console.error("Subscription check failed:", err);
      // Show upgrade prompt if we can't verify
      setShowUpgradePrompt(true);
      return;
    }

    setLoading(true);
    setStep("generating");

    try {
      // 3) Generate greeting-card copy
      const fd1 = new FormData();
      fd1.append("tone", tone);
      fd1.append("recipient", recipient);
      fd1.append("occasion", type);
      const { data: msgData } = await generateMessage(fd1);
      setFrontText(msgData.front_text);
      setInsideText(msgData.inside_text);

      // 4) Build image prompt - always include selections, add personalization if provided
      // CRITICAL: Must leave clear space at top and bottom for text overlays
      const basePrompt = 
        `Professional ${style.toLowerCase()} greeting card design for ${type}. ` +
        `Create a ${tone.toLowerCase()} design focused on ${recipient.toLowerCase()}. ` +
        `CRITICAL LAYOUT REQUIREMENT: This is a greeting card that will have text overlaid on it. ` +
        `You MUST leave the TOP 18% of the image as a clear, simple background area (solid color or subtle pattern) for a headline. ` +
        `You MUST leave the BOTTOM 15% of the image as a clear, simple background area for a subheadline. ` +
        `The main visual content (photos, illustrations, decorations) should be in the CENTER 67% of the image. ` +
        `The top and bottom areas should match the card's color scheme but remain uncluttered for text placement. ` +
        `DO NOT place any faces, important details, or decorative elements in the top or bottom text zones.`;
      
      const builtPrompt = note
        ? `${basePrompt} Additional details: ${note}`
        : `${basePrompt} Include elegant decorative borders that frame the center content area.`;

      console.log("[CardBuilder] Generation details:", {
        type,
        tone, 
        recipient,
        style,
        personalization: note || "(using default prompt)",
        finalPrompt: builtPrompt,
        size,
        hasUploadedPhoto: !!file
      });

      // 5) Fetch images (base + variations OR four independent animations)
      const urls = await createImageJobs({ file, prompt: builtPrompt, size });
      setVariations(urls);
      setStep("variations");
    } catch (err) {
      console.error(err);
      alert("Generation failed. Please try again.");
      setStep("form");
    } finally {
      setLoading(false);
    }
  };

  /* -------------------------------------------------------
     After user picks an image
  ------------------------------------------------------- */
  const handleImageSelect = (selectedUrl) => {
    console.log("[CardBuilder] Image selected:", {
      selectedUrl,
      hasUrl: !!selectedUrl,
      frontText,
      insideText,
      category: type,
      cardSize: size
    });
    
    if (!selectedUrl) {
      console.error("[CardBuilder] No URL provided, returning to form");
      setStep("form");
      return;
    }
    
    // Generate a unique identifier for this AI-generated card
    const aiCardId = `ai_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Ensure we have a full URL for the image
    const fullImageUrl = selectedUrl.startsWith('http') 
      ? selectedUrl 
      : selectedUrl.startsWith('/') 
        ? `${API_URL}${selectedUrl}` 
        : `${API_URL}/${selectedUrl}`;
    
    const navigationState = {
      src: fullImageUrl,
      frontText,
      insideText,
      category: type || "Custom",
      cardSize: size,
      fromAI: true, // Flag to indicate this is AI-generated
      aiGenerated: true, // Additional flag for clarity
    };
    
    console.log("[CardBuilder] Navigating to editing page with state:", navigationState);

    // Persist data so it survives a full page refresh
    try {
      const pendingTemplateData = {
        ...navigationState,
        id: aiCardId,
        category: navigationState.category,
      };
      sessionStorage.setItem("pendingTemplateData", JSON.stringify(pendingTemplateData));
    } catch (e) {
      console.warn("[CardBuilder] Failed to save pending template data:", e);
    }

    try {
      // Navigate to editing page with AI-generated image and text
      // Use "Custom" as category and unique ID for AI-generated cards
      navigate(`/edit/Custom/${aiCardId}`, {
        state: navigationState,
        replace: true // Use replace to prevent back button from returning to generation
      });
      console.log("[CardBuilder] Navigation successful, closing modal");
      onClose();
    } catch (error) {
      console.error("[CardBuilder] Navigation failed:", error);
      alert("Navigation failed. Please try again.");
      return;
    }
  };

  /* -------------------------------------------------------
     Regenerate variations
  ------------------------------------------------------- */
  const handleRegenerate = async () => {
    setLoading(true);
    try {
      // Use the same base prompt with text zone instructions as initial generation
      const basePrompt = 
        `Professional ${style.toLowerCase()} greeting card design for ${type}. ` +
        `Create a ${tone.toLowerCase()} design focused on ${recipient.toLowerCase()}. ` +
        `CRITICAL LAYOUT REQUIREMENT: This is a greeting card that will have text overlaid on it. ` +
        `You MUST leave the TOP 18% of the image as a clear, simple background area (solid color or subtle pattern) for a headline. ` +
        `You MUST leave the BOTTOM 15% of the image as a clear, simple background area for a subheadline. ` +
        `The main visual content (photos, illustrations, decorations) should be in the CENTER 67% of the image. ` +
        `The top and bottom areas should match the card's color scheme but remain uncluttered for text placement. ` +
        `DO NOT place any faces, important details, or decorative elements in the top or bottom text zones.`;
      
      const builtPrompt = note
        ? `${basePrompt} Additional details: ${note}`
        : `${basePrompt} Include elegant decorative borders that frame the center content area.`;

      console.log("[CardBuilder] Regeneration details:", {
        type,
        tone, 
        recipient,
        style,
        personalization: note || "(using default prompt)",
        finalPrompt: builtPrompt,
        size,
        hasUploadedPhoto: !!file
      });

      const urls = await createImageJobs({ file, prompt: builtPrompt, size });
      setVariations(urls);
    } catch (err) {
      console.error("[CardBuilder] Regeneration failed:", err);
      alert("Regeneration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /* -------------------------------------------------------
     Render based on step
  ------------------------------------------------------- */
  if (step === "variations") {
    return (
      <VariationSelector
        variations={variations}
        loading={loading}
        onSelect={handleImageSelect}
        onRegenerate={handleRegenerate}
      />
    );
  }

  if (step === "generating") {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="bg-white rounded-2xl p-10 text-center max-w-md w-full shadow-2xl">
          {/* Large Lottie Animation */}
          <div className="flex justify-center mb-6">
            <div className="w-40 h-40">
              <Lottie 
                animationData={loadingAnimation} 
                loop={true}
                autoplay={true}
              />
            </div>
          </div>
          
          <h2 className="text-2xl font-bold text-gray-800 mb-3">
            ✨ Creating Your Card
          </h2>
          
          <p className="text-lg text-purple-600 font-medium mb-2">
            AI is working its magic...
          </p>
          
          <p className="text-gray-500 text-sm mb-6">
            This typically takes 1-2 minutes. Please don't close this window.
          </p>
          
          {/* Animated progress dots */}
          <div className="flex justify-center gap-2">
            <div className="w-3 h-3 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
            <div className="w-3 h-3 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
            <div className="w-3 h-3 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
          </div>
        </div>
      </div>
    );
  }

  // Upgrade prompt for Preview tier users (no AI credits)
  if (showUpgradePrompt) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 px-6 py-8 text-center">
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-4xl">🎨</span>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Unlock AI Card Creation</h2>
            <p className="text-indigo-100">Create unique, personalized cards with AI</p>
          </div>
          
          {/* Content */}
          <div className="px-6 py-6">
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                <span className="text-xl">✨</span>
                <span className="text-slate-700">AI generates unique card designs</span>
              </div>
              <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                <span className="text-xl">🎯</span>
                <span className="text-slate-700">Personalized for any occasion</span>
              </div>
              <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                <span className="text-xl">⚡</span>
                <span className="text-slate-700">Multiple variations to choose from</span>
              </div>
            </div>
            
            <button
              onClick={() => {
                onClose();
                navigate('/subscribe', {
                  state: {
                    fromFeature: 'ai_generation',
                    message: 'Upgrade to unlock AI-powered card creation!'
                  }
                });
              }}
              className="w-full py-4 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold text-lg rounded-xl hover:from-indigo-600 hover:to-purple-600 transition-all shadow-lg hover:shadow-xl"
            >
              ✨ Upgrade to Create with AI
            </button>
            
            <button
              onClick={() => {
                setShowUpgradePrompt(false);
                onClose();
              }}
              className="w-full mt-3 py-2 text-slate-500 text-sm hover:text-slate-700 transition-colors"
            >
              Maybe later
            </button>
            
            <p className="text-center text-xs text-slate-400 mt-4">
              Or browse our templates to create cards for free!
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Default: form step
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        className="mx-auto w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6"
        style={{ maxHeight: "90vh" }}
      >
        <h2 className="mb-6 text-2xl font-semibold">Create AI Greeting Card</h2>
        
        {/* Authentication Notice */}
        {(!token || !user) && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  <strong>Sign in required!</strong><br />
                  You need to sign in to generate AI greeting cards.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Card Type selector */}
        <label className="mt-6 block text-sm font-medium text-gray-700">
          Card Type
        </label>
        <Dropdown.Root>
          <Dropdown.Trigger asChild>
            <button
              className="mt-1 w-full rounded-md border px-4 py-2 text-left"
              disabled={loading}
            >
              {type || "Select type"}
            </button>
          </Dropdown.Trigger>
          <Dropdown.Content
            className="w-56 rounded-md border bg-white py-1 text-sm shadow-lg"
            sideOffset={4}
          >
            {TYPES.map((t) => (
              <Dropdown.Item
                key={t}
                className="cursor-pointer px-4 py-2 hover:bg-gray-100"
                onSelect={() => setType(t)}
              >
                {t}
              </Dropdown.Item>
            ))}
          </Dropdown.Content>
        </Dropdown.Root>

        {/* Tone buttons */}
        <label className="mt-6 block text-sm font-medium text-gray-700">
          Tone
        </label>
        <div className="mt-2 flex flex-wrap gap-2">
          {TONES.map((t) => (
            <button
              key={t}
              onClick={() => setTone(t)}
              className={`rounded-full border px-3 py-1 text-sm transition ${
                tone === t
                  ? "border-indigo-500 bg-indigo-50 text-indigo-600"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
              disabled={loading}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Recipient buttons */}
        <label className="mt-6 block text-sm font-medium text-gray-700">
          Recipient
        </label>
        <div className="mt-2 flex flex-wrap gap-2">
          {RECIPIENTS.map((r) => (
            <button
              key={r}
              onClick={() => setRec(r)}
              className={`rounded-full border px-3 py-1 text-sm transition ${
                recipient === r
                  ? "border-indigo-500 bg-indigo-50 text-indigo-600"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
              disabled={loading}
            >
              {r}
            </button>
          ))}
        </div>

        {/* Style selector */}
        <label className="mt-6 block text-sm font-medium text-gray-700">
          Style
        </label>
        <div className="mt-2 flex flex-wrap gap-2">
          {STYLES.map((s) => (
            <button
              key={s}
              onClick={() => setStyle(s)}
              className={`rounded-full border px-3 py-1 text-sm transition ${
                style === s
                  ? "border-indigo-500 bg-indigo-50 text-indigo-600"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
              disabled={loading}
            >
              {s}
            </button>
          ))}
        </div>

        {/* Personal note */}
        <label className="mt-6 block text-sm font-medium text-gray-700">
          Personalisation (optional)
        </label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Add a personal note…"
          className="mt-1 w-full rounded-md border px-4 py-2"
          rows={3}
          disabled={loading}
        />

        {/* Photo upload */}
        <label className="mt-6 block text-sm font-medium text-gray-700">
          Or Upload Photo (optional)
        </label>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => setFile(e.target.files[0] || null)}
          className="mt-1 w-full"
          disabled={loading}
        />

        {/* Action buttons */}
        <div className="mt-8 flex justify-end gap-3">
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleGenerate} disabled={loading}>
            {loading ? "Generating…" : (!token || !user) ? "Sign in to Generate" : "Generate"}
          </Button>
        </div>
      </div>
    </div>
  );
}
